import React from "react";
import QuoteForm from "../components/QuoteForm";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const Contact = () => {
  const { t } = useLang();
  const location = useLocation();
  const calculatorData = location.state?.calculator || null;

  return (
    <main className="pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">{t.contact.eyebrow}</p>
          <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-navy-900 text-balance">{t.contact.title}</h1>
          <p className="mt-5 text-slate-600 text-lg max-w-md">{t.contact.subtitle}</p>

          {/* Trust bullets */}
          <ul className="mt-6 space-y-2.5" data-testid="contact-trust-bullets">
            {[t.contact.trust1, t.contact.trust2, t.contact.trust3].map((bullet) => (
              <li key={bullet} className="flex items-center gap-3 text-navy-900 font-medium">
                <span className="w-6 h-6 rounded-full bg-brand-soft text-brand flex items-center justify-center flex-shrink-0">
                  <Check size={14} strokeWidth={3} />
                </span>
                {bullet}
              </li>
            ))}
          </ul>

          <div className="mt-10 space-y-5">
            <InfoRow icon={Phone} label="Bel ons" value="+31 85 250 2086" href="tel:+31852502086" />
            <InfoRow icon={Mail} label="E-mail" value="info@heliosbright.nl" href="mailto:info@heliosbright.nl" />
            <InfoRow icon={MapPin} label="Hoofdkantoor" value="Hereplein 4, 9711 GA Groningen" />
            <InfoRow icon={Clock} label="Openingstijden" value="Ma–Vr: 08:00 – 18:00" />
          </div>

          {calculatorData && (
            <div className="mt-8 bg-brand-soft/40 border border-brand/20 rounded-2xl p-5" data-testid="contact-calculator-summary">
              <div className="text-xs font-bold uppercase tracking-widest text-brand mb-2">Jouw berekening</div>
              <div className="text-sm text-navy-800 leading-relaxed">
                <strong>{calculatorData.panels}</strong> zonnepanelen · <strong>{Number(calculatorData.recommended_battery_kwh ?? calculatorData.battery_capacity_kwh ?? 0)} kWh</strong> thuisbatterij · <strong>€ {Number(calculatorData.estimated_annual_savings || 0).toLocaleString("nl-NL")}</strong> besparing per jaar
              </div>
              {calculatorData.payback_years && (
                <div className="text-sm text-navy-800 mt-1.5">
                  Terugverdientijd: <strong>{calculatorData.payback_years} jaar</strong>
                </div>
              )}
            </div>
          )}
        </div>

        <QuoteForm source={calculatorData ? "calculator_cta" : "contact_page"} calculatorData={calculatorData} />
      </div>
    </main>
  );
};

const InfoRow = ({ icon: Icon, label, value, href }) => {
  const content = (
    <>
      <div className="w-12 h-12 rounded-2xl bg-brand-soft flex items-center justify-center text-brand">
        <Icon size={20} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-semibold text-navy-900">{value}</div>
      </div>
    </>
  );
  if (href) {
    return (
      <a href={href} className="flex items-center gap-4 group hover:opacity-80 transition-opacity" data-testid={`contact-info-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {content}
      </a>
    );
  }
  return <div className="flex items-center gap-4">{content}</div>;
};

export default Contact;
